import { useMemo, useState, type FormEvent } from 'react';
import { Plus } from 'lucide-react';
import {
  useCreateCreative,
  useCreativePreview,
  useCreativeTemplates,
  useCreatives,
  useDeleteCreative,
  useUpdateCreative,
} from '@/api/hooks';
import { errorMessage } from '@/api/client';
import type { CreativeAssets, CreativeCreate, CreativeFormat, CreativeOut } from '@/api/types';
import { CREATIVE_FORMATS } from '@/api/types';
import { Alert } from '@/components/arc/alert/alert';
import { Button } from '@/components/arc/button/button';
import { Checkbox } from '@/components/arc/checkbox/checkbox';
import { ConfirmMorph } from '@/components/arc/confirm-morph/confirm-morph';
import { EmptyState } from '@/components/arc/empty-state/empty-state';
import { Input } from '@/components/arc/input/input';
import { NumberField } from '@/components/arc/number-field/number-field';
import { Select } from '@/components/arc/select/select';
import { Skeleton } from '@/components/arc/skeleton/skeleton';
import { SortableDataTable, type DataColumn } from '@/components/arc/sortable-data-table/sortable-data-table';
import { Textarea } from '@/components/arc/textarea/textarea';
import { CreativeFormatBadge } from '@/components/status/StatusBadge';
import { fmtDate, labelize, truncate } from '@/lib/format';
import { useDebounce } from '@/lib/hooks';
import { useTimezone } from '@/module/MomentProvider';
import styles from './overlay.module.css';

type CreativeRow = { [K in keyof CreativeOut]: CreativeOut[K] };

/** Fields the bundled templates know. Anything else found in a template renders as a plain text input. */
const KNOWN_FIELDS: Record<string, { label: string; kind: 'text' | 'url' | 'color' | 'textarea' }> = {
  headline: { label: 'Headline', kind: 'text' },
  body: { label: 'Body', kind: 'textarea' },
  cta_text: { label: 'Call to action', kind: 'text' },
  cta_url: { label: 'Call to action URL', kind: 'url' },
  image_url: { label: 'Image URL', kind: 'url' },
  logo_url: { label: 'Logo URL', kind: 'url' },
  brand_color: { label: 'Brand color', kind: 'color' },
  price: { label: 'Price', kind: 'text' },
};
const DEFAULT_FIELDS = ['headline', 'body', 'cta_text', 'cta_url', 'image_url', 'logo_url', 'brand_color'];
/** Injected by the ad server at render time, never typed by hand. */
const SERVER_VARS = new Set(['creative_id']);
const DEFAULT_ASSETS: CreativeAssets = {
  headline: 'Your headline',
  body: 'Short supporting copy',
  cta_text: 'Learn more',
  cta_url: 'https://example.com',
  brand_color: '#2563eb',
};

/** `{{variable}}` placeholders in a template, in order of first appearance. */
function templateVars(template: string): string[] {
  const out: string[] = [];
  for (const m of template.matchAll(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g)) {
    const v = m[1];
    if (v && !SERVER_VARS.has(v) && !out.includes(v)) out.push(v);
  }
  return out;
}

export function CreativesPage() {
  const tz = useTimezone();
  const list = useCreatives();
  const templates = useCreativeTemplates();
  const del = useDeleteCreative();
  const [editing, setEditing] = useState<CreativeOut | 'new' | null>(null);

  const items = list.data?.items ?? [];
  const isEmpty = !list.isLoading && !list.isError && items.length === 0;

  const columns: DataColumn<CreativeRow>[] = [
    { key: 'name', label: 'Creative' },
    { key: 'format', label: 'Format', render: (_, c) => <CreativeFormatBadge format={c.format} /> },
    {
      key: 'headline',
      label: 'Headline',
      sortable: false,
      render: (_, c) => {
        const h = c.assets.headline;
        return h ? (
          <span className="text-secondary">{truncate(String(h), 48)}</span>
        ) : (
          <span className="text-muted">No headline</span>
        );
      },
    },
    { key: 'duration_sec', label: 'Duration', numeric: true, render: (v) => `${String(v)} s` },
    { key: 'dismissible', label: 'Dismissible', render: (v) => (v ? 'Yes' : 'No') },
    { key: 'created_at', label: 'Created', render: (v) => fmtDate(String(v), tz) },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      width: 200,
      render: (_, c) => (
        <span className={styles.rowActions}>
          <Button variant="ghost" size="sm" onClick={() => setEditing(c)}>
            Edit
          </Button>
          <ConfirmMorph
            label="Delete"
            prompt={`Delete ${c.name}?`}
            confirmLabel="Delete"
            pendingLabel="Deleting"
            doneLabel="Deleted"
            errorLabel="Still used by a campaign"
            onConfirm={async () => {
              await del.mutateAsync(c.id);
              if (editing !== 'new' && editing?.id === c.id) setEditing(null);
            }}
          />
        </span>
      ),
    },
  ];

  return (
    <>
      <section className="stack-4" aria-labelledby="creatives-heading">
        <div className={styles.sectionHead}>
          <div className={styles.cell}>
            <h2 id="creatives-heading" className={styles.sectionTitle}>
              Creatives
            </h2>
            <span className={styles.sectionMeta}>
              Overlay units rendered on top of the player when a moment matches
            </span>
          </div>
          {!isEmpty && (
            <Button onClick={() => setEditing('new')}>
              <Plus size={16} strokeWidth={1.75} aria-hidden="true" /> New creative
            </Button>
          )}
        </div>

        {list.isLoading ? (
          <Skeleton lines={5} label="Loading creatives" />
        ) : list.isError ? (
          <Alert tone="danger" title="Creatives did not load">
            {errorMessage(list.error)}{' '}
            <Button variant="secondary" size="sm" onClick={() => void list.refetch()}>
              Try again
            </Button>
          </Alert>
        ) : isEmpty ? (
          <EmptyState
            title="No creatives yet"
            description="Pick a format, fill in the copy and preview it over a sample player."
            action={
              <Button onClick={() => setEditing('new')}>
                <Plus size={16} strokeWidth={1.75} aria-hidden="true" /> New creative
              </Button>
            }
          />
        ) : (
          <div className="table-scroll">
            <SortableDataTable<CreativeRow>
              rows={items}
              columns={columns}
              rowKey="id"
              caption="Creatives"
              defaultSort={{ key: 'created_at', direction: 'desc' }}
              itemName={{ one: 'creative', other: 'creatives' }}
            />
          </div>
        )}
      </section>

      {editing && (
        <CreativeEditor
          key={editing === 'new' ? 'new' : editing.id}
          creative={editing === 'new' ? null : editing}
          templates={templates.data ?? {}}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

function CreativeEditor({
  creative,
  templates,
  onClose,
}: {
  creative: CreativeOut | null;
  templates: Record<string, string>;
  onClose: () => void;
}) {
  const create = useCreateCreative();
  const update = useUpdateCreative();
  const [name, setName] = useState(creative?.name ?? '');
  const [format, setFormat] = useState<CreativeFormat>(creative?.format ?? 'banner_bottom');
  const [assets, setAssets] = useState<CreativeAssets>(creative?.assets ?? DEFAULT_ASSETS);
  const [template, setTemplate] = useState(creative?.html_template ?? '');
  // A new creative follows the bundled template for its format until the HTML is edited by hand.
  const [customTemplate, setCustomTemplate] = useState(!!creative?.html_template);
  const [duration, setDuration] = useState(creative?.duration_sec ?? 8);
  const [dismissible, setDismissible] = useState(creative?.dismissible ?? true);
  const [showTemplate, setShowTemplate] = useState(false);

  const effectiveTemplate = customTemplate ? template : (templates[format] ?? '');
  const vars = useMemo(() => templateVars(effectiveTemplate), [effectiveTemplate]);
  const fields = vars.length ? vars : DEFAULT_FIELDS;

  const body = useMemo<CreativeCreate>(
    () => ({
      name: name.trim() || 'preview',
      format,
      html_template: effectiveTemplate || null,
      assets,
      duration_sec: duration,
      dismissible,
    }),
    [name, format, effectiveTemplate, assets, duration, dismissible],
  );
  const preview = useCreativePreview(useDebounce(body, 500));
  const pending = create.isPending || update.isPending;
  const error = create.error ?? update.error;
  const valid = name.trim() !== '';

  const setAsset = (key: string, value: string) => setAssets((prev) => ({ ...prev, [key]: value }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid || pending) return;
    try {
      if (creative) {
        await update.mutateAsync({
          id: creative.id,
          body: {
            name: name.trim(),
            html_template: effectiveTemplate || undefined,
            assets,
            duration_sec: duration,
            dismissible,
          },
        });
      } else {
        await create.mutateAsync({ ...body, name: name.trim() });
      }
      onClose();
    } catch {
      /* shown in the alert below */
    }
  };

  const headingId = `creative-editor-${creative?.id ?? 'new'}`;

  return (
    <section className={styles.editor} aria-labelledby={headingId}>
      <form className={styles.form} onSubmit={(e) => void submit(e)}>
        <div className={styles.cell}>
          <h2 id={headingId} className={styles.sectionTitle}>
            {creative ? `Edit ${creative.name}` : 'New creative'}
          </h2>
          <span className={styles.sectionMeta}>
            {vars.length
              ? `The ${labelize(format).toLowerCase()} template uses ${vars.length} ${vars.length === 1 ? 'field' : 'fields'}.`
              : 'Fill in the fields the template uses.'}
          </span>
        </div>

        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
        <Select
          label="Format"
          value={format}
          onValueChange={(v) => setFormat(v as CreativeFormat)}
          disabled={!!creative}
          description={creative ? 'The format is fixed once a creative exists' : undefined}
          options={CREATIVE_FORMATS.map((f) => ({ value: f, label: labelize(f) }))}
        />

        {fields.map((key) => {
          const meta = KNOWN_FIELDS[key] ?? { label: labelize(key), kind: 'text' as const };
          const value = String(assets[key] ?? '');
          if (meta.kind === 'textarea') {
            return (
              <Textarea
                key={key}
                label={meta.label}
                value={value}
                rows={3}
                onChange={(e) => setAsset(key, e.target.value)}
              />
            );
          }
          return (
            <Input
              key={key}
              label={meta.label}
              type={meta.kind === 'color' ? 'color' : meta.kind === 'url' ? 'url' : 'text'}
              value={meta.kind === 'color' && !value ? '#000000' : value}
              placeholder={meta.kind === 'url' ? 'https://' : undefined}
              onChange={(e) => setAsset(key, e.target.value)}
            />
          );
        })}

        <div className={styles.formGrid2}>
          <NumberField
            label="Duration"
            value={duration}
            onValueChange={setDuration}
            min={1}
            max={120}
            suffix=" s"
          />
          <Checkbox
            label="Dismissible"
            description="Viewers can close it"
            checked={dismissible}
            onCheckedChange={(v) => setDismissible(v === true)}
          />
        </div>

        <div className={styles.formSection}>
          <div className={styles.sectionHead}>
            <span className={styles.subTitle}>HTML template</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowTemplate((s) => !s)}
              aria-expanded={showTemplate}
            >
              {showTemplate ? 'Hide template' : 'Edit template'}
            </Button>
          </div>
          {showTemplate && (
            <div className={styles.templateArea}>
              <Textarea
                label="Template source"
                description={
                  customTemplate
                    ? 'Custom template'
                    : `Bundled ${labelize(format).toLowerCase()} template. Editing it makes a copy.`
                }
                value={effectiveTemplate}
                rows={14}
                spellCheck={false}
                onChange={(e) => {
                  setCustomTemplate(true);
                  setTemplate(e.target.value);
                }}
              />
              {customTemplate && !creative && templates[format] && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setCustomTemplate(false);
                    setTemplate('');
                  }}
                >
                  Reset to bundled template
                </Button>
              )}
            </div>
          )}
        </div>

        {error && (
          <Alert tone="danger" title="Could not save the creative">
            {errorMessage(error)}
          </Alert>
        )}
        <div className={styles.footer}>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={pending} disabled={!valid}>
            {creative ? 'Save creative' : 'Create creative'}
          </Button>
        </div>
      </form>

      <div className={styles.previewPane}>
        <div className={styles.sectionHead}>
          <h3 className={styles.subTitle}>Live preview</h3>
          <span className={styles.sectionMeta} role="status">
            {preview.isFetching
              ? 'Updating'
              : preview.data
                ? 'Rendered by the API'
                : 'Waiting for the first render'}
          </span>
        </div>
        <div className={styles.player} aria-label="Sample player with the creative overlaid">
          <span className={styles.playerLabel} aria-hidden="true">
            Sample player
          </span>
          <span className={styles.playerBar} aria-hidden="true" />
          <div className={styles.slot} data-format={format}>
            {preview.data?.html ? (
              <iframe title="Creative preview" srcDoc={preview.data.html} sandbox="allow-scripts" />
            ) : null}
          </div>
        </div>
        {preview.isError && (
          <Alert tone="danger" title="Preview failed">
            {errorMessage(preview.error)}
          </Alert>
        )}
      </div>
    </section>
  );
}
